import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { COMPOSICOES_CANTEIRO } from "../../../shared/canteiro";
import { ImportarQuantitativos } from "./ImportarQuantitativos";
import type { OrcamentoDetalhe as Detalhe, OrcamentoItemCalculado, Regime, SecaoCalculada } from "../../../shared/tipos";
import { api, baixarArquivo } from "../api/cliente";
import { chaves, useFamilias, useMunicipios, useOrcamento } from "../api/consultas";
import { useAvisos } from "../components/Avisos";
import { MunicipioBusca } from "../components/MunicipioBusca";
import {
  BLOCOS_COMPOSICAO,
  BuscaItensOrcamento,
  FormComposicao,
  ResumoFinanceiro,
  useAtualizarOrcamento,
} from "../components/orcamento";
import { BadgeRegime, Carregando, ErroCarregamento, formatarMoeda, formatarQuantidade, semAutocompletar, Tabela } from "../components/ui";

export function useIdOrcamento(): number {
  const { id } = useParams();
  return Number(id);
}

function EditarConfiguracao({ detalhe }: { detalhe: Detalhe }) {
  const { orcamento } = detalhe;
  const municipios = useMunicipios();
  const familias = useFamilias();
  const atualizar = useAtualizarOrcamento(orcamento.id);
  const { avisar, avisarErro } = useAvisos();
  const [municipioId, setMunicipioId] = useState<number | null>(orcamento.municipio_id);
  const [familiaId, setFamiliaId] = useState(orcamento.familia_id ? String(orcamento.familia_id) : "");
  const [regime, setRegime] = useState<Regime>(orcamento.regime);

  const salvar = useMutation({
    mutationFn: () =>
      api<{ mensagem: string }>(`/api/orcamentos/${orcamento.id}`, {
        method: "PATCH",
        body: { municipio_id: municipioId, familia_id: familiaId, regime },
      }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      atualizar();
    },
    onError: avisarErro,
  });

  return (
    <details className="no-print" style={{ margin: "18px 0" }}>
      <summary className="btn btn-ghost" style={{ padding: "12px 22px", fontSize: "0.95rem" }}>
        Editar Município / Família / Regime
      </summary>
      <div className="card" style={{ marginTop: 8 }}>
        {municipios.isLoading || familias.isLoading ? (
          <Carregando />
        ) : (
          <form
            className="form-row"
            onSubmit={(ev: FormEvent) => {
              ev.preventDefault();
              salvar.mutate();
            }}
          >
            <MunicipioBusca rotulo="Município" municipios={municipios.data ?? []} valor={municipioId} onChange={setMunicipioId} />
            <label>
              Família
              <select value={familiaId} onChange={(e) => setFamiliaId(e.target.value)}>
                <option value="">— não definida —</option>
                {(familias.data ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Regime
              <select value={regime} onChange={(e) => setRegime(e.target.value as Regime)}>
                <option value="desonerada">Desonerada (CPRB)</option>
                <option value="tradicional">Tradicional</option>
              </select>
            </label>
            <button type="submit" disabled={salvar.isPending}>
              Salvar
            </button>
          </form>
        )}
        <p className="note" style={{ marginTop: 8 }}>
          Trocar a família ou o regime não recalcula os itens já adicionados (cada um guarda o preço no momento em que foi
          incluído) — vale conferir/readicionar itens após mudar.
        </p>
      </div>
    </details>
  );
}

function QuantidadeEditavel({ orcamentoId, linhaId, quantidade }: { orcamentoId: number; linhaId: number; quantidade: number }) {
  const [valor, setValor] = useState(String(quantidade));
  const atualizar = useAtualizarOrcamento(orcamentoId);
  const { avisarErro } = useAvisos();
  return (
    <input
      type="text"
      className="no-print"
      style={{ width: 70 }}
      value={valor}
      {...semAutocompletar}
      onChange={(e) => setValor(e.target.value)}
      onBlur={async () => {
        if (valor === String(quantidade)) return;
        try {
          await api(`/api/orcamentos/${orcamentoId}/itens/${linhaId}`, { method: "PATCH", body: { quantidade: valor } });
          atualizar();
        } catch (erro) {
          avisarErro(erro);
          setValor(String(quantidade));
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

export function OrcamentoDetalhe() {
  const id = useIdOrcamento();
  const { data, isLoading, error } = useOrcamento(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const atualizar = useAtualizarOrcamento(id);
  const { avisar, avisarErro } = useAvisos();
  const [baixando, setBaixando] = useState(false);
  const [secaoAtiva, setSecaoAtiva] = useState<number | null>(null);

  const remover = useMutation({
    mutationFn: () => api<{ mensagem: string }>(`/api/orcamentos/${id}`, { method: "DELETE" }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      queryClient.invalidateQueries({ queryKey: chaves.orcamentos });
      queryClient.invalidateQueries({ queryKey: chaves.painel });
      queryClient.removeQueries({ queryKey: chaves.orcamento(id) });
      navigate("/orcamentos");
    },
    onError: avisarErro,
  });

  const removerLinha = useMutation({
    mutationFn: (caminho: string) => api(caminho, { method: "DELETE" }),
    onSuccess: atualizar,
    onError: avisarErro,
  });

  const moverItem = useMutation({
    mutationFn: ({ linhaId, secaoId }: { linhaId: number; secaoId: number | null }) =>
      api(`/api/orcamentos/${id}/itens/${linhaId}`, { method: "PATCH", body: { secao_id: secaoId } }),
    onSuccess: atualizar,
    onError: avisarErro,
  });

  const definirTipo = useMutation({
    mutationFn: ({ linhaId, tipo }: { linhaId: number; tipo: "material" | "servico" }) =>
      api(`/api/orcamentos/${id}/itens/${linhaId}`, { method: "PATCH", body: { tipo } }),
    onSuccess: atualizar,
    onError: avisarErro,
  });

  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;
  const { orcamento, resumo, pode_gerenciar: podeGerenciar } = data;

  // Agrupa os itens por seção (mantém seções vazias, para dar onde adicionar).
  const semSecao = resumo.itens.filter((it) => it.secao_id == null);
  const gruposItens: { chave: string; secao: SecaoCalculada | null; rotuloSem?: boolean; itens: OrcamentoItemCalculado[] }[] =
    resumo.secoes.length === 0
      ? [{ chave: "todos", secao: null, itens: resumo.itens }]
      : [
          ...resumo.secoes.map((sec) => ({
            chave: `s${sec.id}`,
            secao: sec,
            itens: resumo.itens.filter((it) => it.secao_id === sec.id),
          })),
          ...(semSecao.length ? [{ chave: "sem", secao: null, rotuloSem: true, itens: semSecao }] : []),
        ];

  const linhaItem = (it: OrcamentoItemCalculado, numero: string) => (
    <tr key={it.id}>
      <td>
        {numero && <span className="note">{numero} </span>}
        {it.codigo_snapshot}
      </td>
      <td>
        {it.descricao_snapshot}
      </td>
      <td>{it.unidade_snapshot}</td>
      <td>
        {podeGerenciar ? (
          <>
            <select
              className="no-print"
              value={it.tipo_bdi === "materiais" ? "material" : "servico"}
              title="Material vai para a seção Materiais (criada se não existir)"
              disabled={definirTipo.isPending}
              onChange={(e) => definirTipo.mutate({ linhaId: it.id, tipo: e.target.value as "material" | "servico" })}
            >
              <option value="servico">Serviço</option>
              <option value="material">Material</option>
            </select>
            <span className="only-print">{it.tipo_bdi === "materiais" ? "Material" : "Serviço"}</span>
          </>
        ) : it.tipo_bdi === "materiais" ? (
          "Material"
        ) : (
          "Serviço"
        )}
      </td>
      <td className="num">
        {podeGerenciar ? (
          <>
            <QuantidadeEditavel key={it.quantidade} orcamentoId={id} linhaId={it.id} quantidade={it.quantidade} />
            <span className="only-print">{formatarQuantidade(it.quantidade)}</span>
          </>
        ) : (
          formatarQuantidade(it.quantidade)
        )}
      </td>
      <td className="num">{formatarMoeda(it.preco_unitario)}</td>
      <td className="num">{formatarMoeda(it.total)}</td>
      <td className="no-print">
        {podeGerenciar && (
          <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end" }}>
            {resumo.secoes.length > 0 && (
              <select
                value={it.secao_id ?? ""}
                title="Mover para seção"
                onChange={(e) => moverItem.mutate({ linhaId: it.id, secaoId: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">Sem seção</option>
                {resumo.secoes.map((sec) => (
                  <option key={sec.id} value={sec.id}>
                    {sec.numero} {sec.titulo}
                  </option>
                ))}
              </select>
            )}
            <button
              className="btn-danger"
              type="button"
              disabled={removerLinha.isPending}
              onClick={() => removerLinha.mutate(`/api/orcamentos/${id}/itens/${it.id}`)}
            >
              remover
            </button>
          </div>
        )}
      </td>
    </tr>
  );

  return (
    <>
      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>{orcamento.nome}</h1>
          <p className="note">
            {resumo.municipio && (
              <>
                Município: <strong>{resumo.municipio.nome}</strong> ·{" "}
              </>
            )}
            Família: <strong>{resumo.familia_nome || "não definida"}</strong> · Regime: <BadgeRegime regime={orcamento.regime} />{" "}
            {resumo.iss_pct === null && (
              <span className="badge badge-muted">município sem ISS/sindicato configurado — BDI não pode ser calculado</span>
            )}{" "}
            {!orcamento.familia_id && <span className="badge badge-muted">sem família definida — preços podem ficar ambíguos entre tabelas</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {podeGerenciar && (
            <Link className="btn btn-orange" to={`/orcamentos/${id}/compor`}>
              Compor Serviço
            </Link>
          )}
          <Link className="btn btn-ghost" to={`/orcamentos/${id}/canteiro`}>
            Canteiro de Obras
          </Link>
          <Link className="btn btn-ghost" to={`/orcamentos/${id}/curva-abc`}>
            Curva ABC
          </Link>
          <Link className="btn btn-ghost" to={`/orcamentos/${id}/cronograma`}>
            Cronograma físico-financeiro
          </Link>
          <Link className="btn btn-ghost" to={`/orcamentos/${id}/modelos`}>
            Modelos de Referência
          </Link>
          <a className="btn btn-ghost" href={`/orcamentos/${id}/imprimir`} target="_blank" rel="noreferrer">
            Imprimir / PDF
          </a>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={baixando}
            onClick={async () => {
              setBaixando(true);
              try {
                await baixarArquivo(`/api/orcamentos/${id}/exportar.xlsx`, `orcamento_${id}.xlsx`);
              } catch (erro) {
                avisarErro(erro);
              } finally {
                setBaixando(false);
              }
            }}
          >
            Exportar Excel
          </button>
          {podeGerenciar && (
            <button
              type="button"
              className="btn-danger"
              disabled={remover.isPending}
              onClick={() => {
                if (confirm("Excluir este orçamento? Essa ação não pode ser desfeita.")) remover.mutate();
              }}
            >
              Excluir orçamento
            </button>
          )}
        </div>
      </div>

      {!podeGerenciar && (
        <p className="note" style={{ marginTop: 10 }}>
          Este orçamento pertence a outro usuário (ou sua conta é somente leitura) — você pode consultar, mas apenas quem o
          criou ou o administrador pode alterá-lo ou excluí-lo.
        </p>
      )}

      {podeGerenciar && <EditarConfiguracao key={`${orcamento.municipio_id}-${orcamento.familia_id}-${orcamento.regime}`} detalhe={data} />}
      {podeGerenciar && (
        <GerenciarSecoes
          orcamentoId={id}
          secoes={resumo.secoes}
          secaoAtiva={secaoAtiva}
          onSecaoAtiva={setSecaoAtiva}
          atualizar={atualizar}
          avisarErro={avisarErro}
        />
      )}
      {podeGerenciar && <ImportarQuantitativos orcamentoId={id} secaoAtiva={secaoAtiva} secoes={resumo.secoes} />}
      {podeGerenciar && (
        <BuscaItensOrcamento orcamentoId={id} secoes={resumo.secoes} secaoAtiva={secaoAtiva} onSecaoAtiva={setSecaoAtiva} />
      )}

      <h2>1. Orçamento</h2>
      <div className="card">
        <Tabela>
          <thead>
            <tr>
              <th>Código</th>
              <th>Descrição</th>
              <th>Un.</th>
              <th>Tipo</th>
              <th className="num">Qtd.</th>
              <th className="num">Preço Unit.</th>
              <th className="num">Total</th>
              <th className="no-print" />
            </tr>
          </thead>
          <tbody>
            {resumo.itens.length === 0 && resumo.secoes.length === 0 ? (
              <tr>
                <td colSpan={8} className="note">
                  Nenhum item adicionado ainda.
                </td>
              </tr>
            ) : (
              gruposItens.map((g) => (
                <Fragment key={g.chave}>
                  {g.secao && (
                    <tr className={g.secao.nivel === 2 ? "linha-secao linha-subsecao" : "linha-secao"}>
                      <td colSpan={6}>
                        <strong>{g.secao.numero}</strong> {g.secao.titulo}
                      </td>
                      <td className="num" style={{ fontWeight: 700 }}>
                        {g.secao.total_itens > 0 ? formatarMoeda(g.secao.total_itens) : ""}
                      </td>
                      <td className="no-print" />
                    </tr>
                  )}
                  {g.rotuloSem && (
                    <tr className="linha-secao">
                      <td colSpan={8}>Sem seção</td>
                    </tr>
                  )}
                  {g.itens.map((it, i) => linhaItem(it, g.secao ? `${g.secao.numero}.${i + 1}` : ""))}
                </Fragment>
              ))
            )}
            {resumo.itens.length > 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: "right", fontWeight: 700 }}>
                  Subtotal
                </td>
                <td className="num" style={{ fontWeight: 700 }}>
                  {formatarMoeda(resumo.subtotal_materiais + resumo.subtotal_servicos)}
                </td>
                <td className="no-print" />
              </tr>
            )}
          </tbody>
        </Tabela>
        <p className="note">
          Tipo: ao marcar "Material", o item vai para a seção Materiais (criada se não existir); ao voltar para "Serviço", sai dela. Contam como material os itens da seção cujo nome começa com "Materia" (e das subseções dela); os demais, como
          material. Sobre cada grupo incide o BDI correspondente.
        </p>
      </div>

      {resumo.itens_cotacao.length > 0 && (
        <section>
          <h2>Itens para cotação</h2>
          <p className="note">
            Itens da planilha importada que não têm preço na tabela EMBASA deste orçamento. Faça a cotação externa e adicione
            manualmente pela busca acima (ou em Compor Serviço) quando tiver o valor.
          </p>
          <div className="card">
            <Tabela>
              <thead>
                <tr>
                  <th>Código (planilha)</th>
                  <th>Descrição</th>
                  <th>Un.</th>
                  <th className="num">Qtd.</th>
                  <th className="no-print" />
                </tr>
              </thead>
              <tbody>
                {resumo.itens_cotacao.map((ic) => (
                  <tr key={ic.id}>
                    <td>{ic.codigo_planilha || "—"}</td>
                    <td>{ic.descricao}</td>
                    <td>{ic.unidade || "—"}</td>
                    <td className="num">{formatarQuantidade(ic.quantidade)}</td>
                    <td className="no-print">
                      {podeGerenciar && (
                        <button
                          className="btn-danger"
                          type="button"
                          disabled={removerLinha.isPending}
                          onClick={() => removerLinha.mutate(`/api/orcamentos/${id}/cotacao/${ic.id}`)}
                        >
                          remover
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
          </div>
        </section>
      )}

      {BLOCOS_COMPOSICAO.map(({ tipo, rotulo, dica }) => (
        <section key={tipo}>
          <h2>{rotulo}</h2>
          <p className="note">
            <Link to={`/orcamentos/${id}/canteiro`}>
              <strong>Compor pelo canteiro de obras</strong>
            </Link>{" "}
            (memória de cálculo + composição {COMPOSICOES_CANTEIRO[tipo].codigo.replace("/EMBASA", "")}) ou lance linhas avulsas abaixo.{" "}
            {dica} Veja o checklist completo com fonte citada em{" "}
            <Link to={`/orcamentos/${id}/modelos`}>Modelos de Referência → Mobilização, Desmobilização e Administração Local</Link>.
          </p>
          <div className="card">
            {podeGerenciar && <FormComposicao orcamentoId={id} tipo={tipo} />}
            <Tabela>
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Código ref.</th>
                  <th>Un.</th>
                  <th className="num">Qtd.</th>
                  <th className="num">Valor unit.</th>
                  <th className="num">Total</th>
                  <th className="no-print" />
                </tr>
              </thead>
              <tbody>
                {resumo.blocos_especiais[tipo].length ? (
                  resumo.blocos_especiais[tipo].map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.descricao}
                        {c.origem === "canteiro" && (
                          <>
                            {" "}
                            <span className="badge badge-blue" title="Gerada pela tela Canteiro de Obras — altere por lá">
                              canteiro
                            </span>
                          </>
                        )}
                      </td>
                      <td>{c.codigo_referencia || "—"}</td>
                      <td>{c.unidade || "—"}</td>
                      <td className="num">{formatarQuantidade(c.quantidade)}</td>
                      <td className="num">{formatarMoeda(c.valor_unitario)}</td>
                      <td className="num">{formatarMoeda(c.total)}</td>
                      <td className="no-print">
                        {podeGerenciar && (
                          <button
                            className="btn-danger"
                            type="button"
                            disabled={removerLinha.isPending}
                            onClick={() => removerLinha.mutate(`/api/orcamentos/${id}/composicao/${c.id}`)}
                          >
                            remover
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="note">
                      Nenhum item.
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
                    Subtotal
                  </td>
                  <td className="num" style={{ fontWeight: 700 }}>
                    {formatarMoeda(resumo.totais_especiais[tipo])}
                  </td>
                  <td className="no-print" />
                </tr>
              </tbody>
            </Tabela>
          </div>
        </section>
      ))}

      <h2>5. Resumo financeiro (BDI conforme tabela oficial)</h2>
      <ResumoFinanceiro resumo={resumo} />
    </>
  );
}

/** Campo de título da seção com estado próprio (não perde o foco durante a digitação). */
function TituloSecao({ inicial, onSalvar }: { inicial: string; onSalvar: (titulo: string) => void }) {
  const [texto, setTexto] = useState(inicial);
  return (
    <input
      type="text"
      value={texto}
      style={{ minWidth: 220, fontWeight: 600 }}
      {...semAutocompletar}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => {
        const t = texto.trim();
        if (t && t !== inicial) onSalvar(t);
        else setTexto(inicial);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/** Gerência das seções/subseções: criar, renomear, reordenar, remover e escolher onde adicionar itens. */
function GerenciarSecoes({
  orcamentoId,
  secoes,
  secaoAtiva,
  onSecaoAtiva,
  atualizar,
  avisarErro,
}: {
  orcamentoId: number;
  secoes: SecaoCalculada[];
  secaoAtiva: number | null;
  onSecaoAtiva: (id: number | null) => void;
  atualizar: () => void;
  avisarErro: (erro: unknown) => void;
}) {
  const mutar = useMutation({
    mutationFn: ({ metodo, caminho, corpo }: { metodo: string; caminho: string; corpo?: unknown }) =>
      api(caminho, { method: metodo, body: corpo }),
    onSuccess: atualizar,
    onError: avisarErro,
  });
  const base = `/api/orcamentos/${orcamentoId}/secoes`;
  const criar = (paiId?: number) => mutar.mutate({ metodo: "POST", caminho: base, corpo: { titulo: "", pai_id: paiId ?? null } });
  const renomear = (sid: number, titulo: string) => mutar.mutate({ metodo: "PATCH", caminho: `${base}/${sid}`, corpo: { titulo } });
  const mover = (sid: number, direcao: "cima" | "baixo") => mutar.mutate({ metodo: "POST", caminho: `${base}/${sid}/mover`, corpo: { direcao } });
  const remover = (sid: number) => {
    if (confirm("Remover esta seção? Os itens dela ficam sem seção (não são apagados).")) {
      mutar.mutate({ metodo: "DELETE", caminho: `${base}/${sid}` });
    }
  };
  const adicionarAqui = (sid: number) => {
    onSecaoAtiva(sid);
    document.getElementById("busca-itens")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <details className="card no-print" style={{ marginBottom: 18 }} open={secoes.length > 0}>
      <summary className="btn btn-ghost" style={{ padding: "12px 22px", fontSize: "0.95rem" }}>
        Seções do orçamento {secoes.length > 0 ? `(${secoes.filter((s) => s.nivel === 1).length})` : ""}
      </summary>
      <div style={{ marginTop: 14 }}>
        <p className="note">
          Separe o orçamento em seções e subseções numeradas. Use <strong>Adicionar itens aqui</strong> para escolher onde os
          próximos itens (da busca ou da importação) vão entrar.
        </p>
        {secoes.map((sec) => (
          <div
            key={sec.id}
            className={`canteiro-resultado${sec.id === secaoAtiva ? " canteiro-faixa-atual" : ""}`}
            style={{ borderTop: sec.nivel === 1 ? "2px solid var(--border)" : "1px dashed var(--border)", paddingLeft: sec.nivel === 2 ? 24 : 0 }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong style={{ minWidth: 34 }}>{sec.numero}</strong>
              <TituloSecao inicial={sec.titulo} onSalvar={(t) => renomear(sec.id, t)} />
            </span>
            <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <button type="button" className="btn-ghost" title="Subir" onClick={() => mover(sec.id, "cima")}>
                ↑
              </button>
              <button type="button" className="btn-ghost" title="Descer" onClick={() => mover(sec.id, "baixo")}>
                ↓
              </button>
              {sec.nivel === 1 && (
                <button type="button" className="btn-ghost" onClick={() => criar(sec.id)}>
                  + subseção
                </button>
              )}
              <button type="button" className="btn-orange" onClick={() => adicionarAqui(sec.id)}>
                Adicionar itens aqui
              </button>
              <button type="button" className="btn-danger" onClick={() => remover(sec.id)}>
                remover
              </button>
            </span>
          </div>
        ))}
        <button type="button" className="btn-orange" style={{ marginTop: 12 }} disabled={mutar.isPending} onClick={() => criar()}>
          + Adicionar seção
        </button>
      </div>
    </details>
  );
}
